import { supabase } from "@/integrations/supabase/client";

/**
 * Logger błędów frontendu - zapisuje do tabeli `client_error_log`.
 * Widoczne dla admina w zakładce „Błędy".
 *
 * Zasady:
 * - throttling: max 20 wpisów/min na sesję
 * - dedup: ten sam (message+stack-head) maks 1x na 60s
 * - ignorowanie znanego szumu (ResizeObserver, ChunkLoadError, AbortError)
 */

const MAX_PER_MINUTE = 20;
const DEDUP_WINDOW_MS = 60_000;
const IGNORE_PATTERNS = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /AbortError/i,
  /ChunkLoadError/i, // user network / cache - nie warto
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /NetworkError when attempting to fetch resource/i,
];

const sentTimestamps: number[] = [];
const recentHashes = new Map<string, number>();

const hash = (s: string): string => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return String(h);
};

const getVisitorId = (): string | null => {
  try {
    return localStorage.getItem("visitor_id");
  } catch {
    return null;
  }
};

const truncate = (s: string | undefined | null, n: number): string | null => {
  if (!s) return null;
  return s.length > n ? s.slice(0, n) : s;
};

export type ClientErrorPayload = {
  message: string;
  stack?: string;
  source?: "frontend" | "edge_function" | "manual";
  level?: "error" | "warning" | "info";
  metadata?: Record<string, unknown>;
};

export async function logClientError(p: ClientErrorPayload): Promise<void> {
  try {
    const msg = (p.message || "").toString().trim();
    if (!msg) return;

    if (IGNORE_PATTERNS.some((r) => r.test(msg))) return;

    // throttle - max N/min
    const now = Date.now();
    while (sentTimestamps.length && now - sentTimestamps[0] > 60_000) {
      sentTimestamps.shift();
    }
    if (sentTimestamps.length >= MAX_PER_MINUTE) return;

    // dedup
    const sig = hash(msg + "|" + (p.stack?.split("\n").slice(0, 2).join("|") ?? ""));
    const last = recentHashes.get(sig);
    if (last && now - last < DEDUP_WINDOW_MS) return;
    recentHashes.set(sig, now);
    // prune
    if (recentHashes.size > 200) {
      const cutoff = now - DEDUP_WINDOW_MS;
      for (const [k, v] of recentHashes) {
        if (v < cutoff) recentHashes.delete(k);
      }
    }
    sentTimestamps.push(now);

    const { data: { session } } = await supabase.auth.getSession().catch(() => ({ data: { session: null } as any }));

    await supabase.from("client_error_log").insert({
      source: p.source ?? "frontend",
      level: p.level ?? "error",
      message: truncate(msg, 2000) as string,
      stack: truncate(p.stack ?? null, 8000),
      url: truncate(typeof window !== "undefined" ? window.location.href : null, 1000),
      user_agent: truncate(typeof navigator !== "undefined" ? navigator.userAgent : null, 500),
      visitor_id: getVisitorId(),
      user_id: session?.user?.id ?? null,
      metadata: (p.metadata ?? null) as any,
    });
  } catch {
    // never throw from the error logger
  }
}

let installed = false;

export function installGlobalErrorReporter(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (ev: ErrorEvent) => {
    const err = ev.error;
    void logClientError({
      message: err?.message || ev.message || "Unknown error",
      stack: err?.stack,
      metadata: {
        kind: "window.error",
        filename: ev.filename,
        lineno: ev.lineno,
        colno: ev.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    const r: any = ev.reason;
    const message =
      r?.message ||
      (typeof r === "string" ? r : "") ||
      "Unhandled promise rejection";
    void logClientError({
      message,
      stack: r?.stack,
      metadata: { kind: "unhandledrejection" },
    });
  });
}

/**
 * Wrapper dla supabase.functions.invoke który dodatkowo loguje błędy
 * z kontekstem (nazwa funkcji + ew. statusCode). Używaj świadomie w
 * krytycznych ścieżkach (claim, owner-save), żeby nie zapychać logu
 * spodziewanymi 401/403.
 */
export async function invokeWithErrorLog<T = unknown>(
  fn: string,
  options?: Parameters<typeof supabase.functions.invoke>[1],
): Promise<{ data: T | null; error: any }> {
  const res = await supabase.functions.invoke(fn, options);
  if (res.error) {
    void logClientError({
      source: "edge_function",
      message: `[${fn}] ${(res.error as any)?.message || "edge function error"}`,
      metadata: {
        function: fn,
        status: (res.error as any)?.context?.status ?? null,
      },
    });
  }
  return res as { data: T | null; error: any };
}
